import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import ErrorIcon from 'assets/images/error.react.svg';
import LockIcon from 'assets/images/lock.react.svg';
import { PaperContent } from 'view/layout/paper';
import { ExperienceFailure } from '@atlassian/help-center-common-component/analytics';
import { HTTP } from '@atlassian/help-center-common-component/constants';
import { DocumentTitle } from '@atlassian/help-center-common-component/document-title';
import { ErrorLink } from './error-link';
import messages from './messages';
import { Message, Description, Container, IconContainer } from './styled';
import type { PersistedError } from 'state/persisted/types';

interface ErrorPageProps extends Partial<PersistedError> {
    children?: React.ReactNode;
}

const isUnhandledError = (statusCode: number) => {
    // We want to report on any 5xx errors for the experience tracker.
    return `${statusCode}`.startsWith('5');
};

export const ErrorPage: React.FC<ErrorPageProps & InjectedIntlProps> = ({ error, children, intl }) => {
    if (!!error) {
        const status = error.status;
        let Icon: React.ComponentType;
        let heading: string = '';
        let description: string = '';

        switch (status) {
            case HTTP.FORBIDDEN: {
                heading = intl.formatMessage(messages.permissionDenied);
                description = intl.formatMessage(messages.permissionDeniedMessage);
                Icon = LockIcon;
                break;
            }

            case HTTP.NOT_FOUND: {
                heading = intl.formatMessage(messages.notFound);
                description = intl.formatMessage(messages.notFoundMessage);
                Icon = ErrorIcon;
                break;
            }

            case HTTP.OFFLINE: {
                heading = intl.formatMessage(messages.offlineHeading);
                description = intl.formatMessage(messages.offlineMessage);
                Icon = ErrorIcon;
                break;
            }

            case HTTP.SERVER:
            default: {
                heading = intl.formatMessage(messages.genericHeading);
                description = intl.formatMessage(messages.genericMessage);
                Icon = ErrorIcon;
                break;
            }
        }

        if (error.message) {
            // If there is a message with the error we override it.
            description = typeof error.message === 'string' ? error.message : intl.formatMessage(error.message);
        }

        return (
            <DocumentTitle title={description}>
                <PaperContent>
                    <Container>
                        <IconContainer>
                            <Icon />
                        </IconContainer>

                        <Message>{heading}</Message>

                        <Description>{description}</Description>

                        {status !== HTTP.OFFLINE && (
                            <ErrorLink callToActionUrl={error.callToActionUrl}>{error.callToActionText}</ErrorLink>
                        )}

                        {isUnhandledError(status) && (
                            <ExperienceFailure location="error-page" errorMessage={description} />
                        )}
                    </Container>
                </PaperContent>
            </DocumentTitle>
        );
    }

    return React.Children.only(children) as JSX.Element;
};

const IntlErrorPage = injectIntl(ErrorPage);

export const withErrorPage =
    <TProps extends object>(WrappedComponent: React.ComponentType<TProps>) =>
    // upgrading eslint-plugin-react to 7.31.11. Please correct when this code is revisited.
    // eslint-disable-next-line react/display-name
    (props: TProps & Partial<PersistedError>) =>
        (
            <IntlErrorPage {...props} error={props.error}>
                <WrappedComponent {...props} />
            </IntlErrorPage>
        );

export const defaultErrorPayload = {
    status: 500,
    callToActionUrl: '',
    callToActionText: '',
    message: '',
};

export default IntlErrorPage;
